import type { TaskStatus } from "@/lib/project-types";

export type TaskDueFilter = "all" | "overdue" | "upcoming" | "no_date";
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDate(value: unknown): value is string {
  if (typeof value !== "string" || !CALENDAR_DATE.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day;
}

/** Format a date without ever interpreting it as an instant in the local timezone. */
export function formatTaskDueDate(value: string, locale: string): string {
  if (!isCalendarDate(value)) return value;
  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(year, month - 1, day, 12)));
}

export function todayInTimezone(timezone: string, now = new Date()): string {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  }
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function isTaskOverdue(dueDate: string | null | undefined, status: TaskStatus, today: string): boolean {
  return isCalendarDate(dueDate) && dueDate < today && status !== "done" && status !== "cancelled";
}

export function dueDateSupported(response: { due_date_supported?: boolean; tasks?: { due_date?: string | null }[] }): boolean {
  return response.due_date_supported === true
    && (!response.tasks || response.tasks.every((task) => Object.prototype.hasOwnProperty.call(task, "due_date")));
}
